import { useEffect, useState } from 'react'
import Icon from './Icon'

/**
 * ☕ 「请我喝咖啡」按钮 —— 展示个人微信收款码
 *
 * - 触发按钮：放在 Header (HomePage 顶部) 与 ThemeToggle 并排
 * - 弹窗：展示 /donate/wechat.png，Z 哥口吻文案，可 Esc / 点击背景关闭
 * - 费用纯属自愿；不要任何 pushy 措辞
 */
export function CoffeeButton({ className = '' }: { className?: string }) {
  const [open, setOpen] = useState(false)

  // Esc 关闭
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="请我喝咖啡"
        title="请我喝咖啡"
        className={`pixel-button pixel-tag inline-flex items-center gap-1.5 ${className}`}
        style={{ fontSize: '11px', padding: '6px 10px', minWidth: 'auto' }}
      >
        <Icon name="teacup" size={14} />
        <span>请咖啡</span>
      </button>

      {open && <CoffeeDialog onClose={() => setOpen(false)} />}
    </>
  )
}

function CoffeeDialog({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="请我喝咖啡"
    >
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative pixel-box pixel-box-bright w-full max-w-xs p-4 max-h-[90vh] overflow-y-auto">
        {/* 标题行 */}
        <div className="flex items-center justify-between mb-3">
          <div className="font-pixel text-[11px] text-[color:var(--color-gold)] tracking-wider inline-flex items-center gap-1.5">
            <Icon name="teacup" size={14} />
            请我喝杯咖啡
          </div>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="text-[color:var(--color-text-dim)] hover:text-[color:var(--color-bear)] transition-colors px-1"
          >
            <Icon name="cross" size={11} />
          </button>
        </div>

        {/* 正文 —— Z 哥口吻：硬但不命令式 */}
        <p className="font-pixel-body text-sm text-[color:var(--color-text)] leading-relaxed mb-1">
          交易员的燃料，干了这杯继续看盘。
        </p>
        <p className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)] mb-3">
          ► 识别下方二维码 · 微信赞赏码
        </p>

        {/* 二维码图片 —— 限高避免撑炸弹窗; 浏览器默认 anti-alias, 不强制 pixelated 让扫码边缘干净 */}
        <div className="mb-3">
          <img
            src="/donate/wechat.png"
            alt="微信赞赏码"
            className="block w-full h-auto mx-auto"
            style={{ maxHeight: '280px', maxWidth: '280px' }}
            onError={(e) => {
              const target = e.currentTarget
              target.style.display = 'none'
              const fallback = target.nextElementSibling as HTMLElement | null
              if (fallback) fallback.style.display = 'block'
            }}
          />
          <div style={{ display: 'none' }} className="p-4 text-center font-pixel-body text-xs text-[color:var(--color-bear)]">
            二维码加载失败，请稍后再试
          </div>
        </div>

        {/* 收尾 —— 自然不强迫 */}
        <p className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)] leading-relaxed mb-3">
          ► 纯属自愿。也可以逛逛{' '}
          <a
            href="https://zknowledge.site/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[color:var(--color-bull)] hover:underline"
          >
            Z 哥知识库
          </a>
          {' '}找干货。
        </p>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="pixel-button pixel-button-primary w-full"
        >
          ► 收下这份心意
        </button>
      </div>
    </div>
  )
}

export default CoffeeButton
